import { render } from 'preact';
import { Router, Route, Redirect } from 'wouter';
import { useLocation } from 'wouter';
import type { JSX } from 'preact';
import { useEffect } from 'preact/hooks';

import { Layout } from './components/Layout';
import { lazyPage, matchesPath } from './lazyPage';
import type { LazyPage } from './lazyPage';

import solutionsData from './data/solutions.json';
import platformPagesData from './data/platform-pages.json';
import { useRegion } from './hooks/useRegion';
import type { Region } from './hooks/useRegion';
import { useAnalytics } from './hooks/useAnalytics';

const sectorSlugs = (solutionsData as { slug: string; type?: string; publish?: boolean }[])
  .filter((s) => s.type === 'discipline' && s.publish !== false)
  .map((s) => s.slug)
  // /government has a bespoke page component (see Government route below)
  .filter((slug) => slug !== 'government');

const pillarSlugs = (solutionsData as { slug: string; type?: string; publish?: boolean }[])
  .filter((s) => s.type === 'pillar' && s.publish !== false)
  .map((s) => s.slug);

// Platform pages driven from structure.md → platform-pages.json
const platformPageSlugs = (platformPagesData as { slug: string }[]).map((p) => p.slug);

import './styles/variables.css';
import './styles/base.css';
import './styles/components.css';
import './styles/layout.css';
import './styles/pages.css';
import './styles/blog.css';
import './styles/paper-pages.css';
import './styles/print.css';

// Every page is its own chunk, so a visitor downloads the code for the page
// they are on rather than the whole site.
const Home = lazyPage(() => import('./pages/Home').then((m) => m.Home));
const Enterprise = lazyPage(() => import('./pages/Enterprise').then((m) => m.Enterprise));
const UseCases = lazyPage(() => import('./pages/UseCases').then((m) => m.UseCases));
const UseCaseDetail = lazyPage(() => import('./pages/UseCaseDetail').then((m) => m.UseCaseDetail));
const PlatformDetail = lazyPage(() => import('./pages/PlatformDetail').then((m) => m.PlatformDetail));
const Apps = lazyPage(() => import('./pages/Apps').then((m) => m.Apps));
const Demo = lazyPage(() => import('./pages/Demo').then((m) => m.Demo));
const Blog = lazyPage(() => import('./pages/Blog').then((m) => m.Blog));
const BlogPost = lazyPage(() => import('./pages/BlogPost').then((m) => m.BlogPost));
const Contact = lazyPage(() => import('./pages/Contact').then((m) => m.Contact));
const Eula = lazyPage(() => import('./pages/Eula').then((m) => m.Eula));
const LandIqEula = lazyPage(() => import('./pages/LandIqEula').then((m) => m.LandIqEula));
const CustomerAppPolicy = lazyPage(() => import('./pages/CustomerAppPolicy').then((m) => m.CustomerAppPolicy));
const PrivacyPolicy = lazyPage(() => import('./pages/PrivacyPolicy').then((m) => m.PrivacyPolicy));
const PricingDraft = lazyPage(() => import('./pages/PricingDraft').then((m) => m.PricingDraft));
const AppStoreDraft = lazyPage(() => import('./pages/AppStoreDraft').then((m) => m.AppStoreDraft));
const FlowDraft = lazyPage(() => import('./pages/FlowDraft').then((m) => m.FlowDraft));
const HomeDraft = lazyPage(() => import('./pages/HomeDraft').then((m) => m.HomeDraft));
const HomeDraft2 = lazyPage(() => import('./pages/HomeDraft2').then((m) => m.HomeDraft2));
const HomeDraft3 = lazyPage(() => import('./pages/HomeDraft3').then((m) => m.HomeDraft3));
const Careers = lazyPage(() => import('./pages/Careers').then((m) => m.Careers));
const RoleDetail = lazyPage(() => import('./pages/RoleDetail').then((m) => m.RoleDetail));
const Security = lazyPage(() => import('./pages/Security').then((m) => m.Security));
const SiteGuide = lazyPage(() => import('./pages/SiteGuide').then((m) => m.SiteGuide));
const Team = lazyPage(() => import('./pages/Team').then((m) => m.Team));
const CaseStudies = lazyPage(() => import('./pages/CaseStudies').then((m) => m.CaseStudies));
const CaseStudyDetail = lazyPage(() => import('./pages/CaseStudyDetail').then((m) => m.CaseStudyDetail));
const AiOrchestration = lazyPage(() => import('./pages/AiOrchestration').then((m) => m.AiOrchestration));
const LandIQ = lazyPage(() => import('./pages/LandIQ').then((m) => m.LandIQ));
const GiraffeAppPlatform = lazyPage(() => import('./pages/GiraffeAppPlatform').then((m) => m.GiraffeAppPlatform));
const Government = lazyPage(() => import('./pages/Government').then((m) => m.Government));
const GiraffeDesign = lazyPage(() => import('./pages/GiraffeDesign').then((m) => m.GiraffeDesign));
const WorkWithGiraffe = lazyPage(() => import('./pages/WorkWithGiraffe').then((m) => m.WorkWithGiraffe));
const GiraffeGovernment = lazyPage(() => import('./pages/GiraffeGovernment').then((m) => m.GiraffeGovernment));
const GiraffeCode = lazyPage(() => import('./pages/GiraffeCode').then((m) => m.GiraffeCode));
const GiraffeDeveloper = lazyPage(() => import('./pages/GiraffeDeveloper').then((m) => m.GiraffeDeveloper));
const GiraffeFactory = lazyPage(() => import('./pages/GiraffeFactory').then((m) => m.GiraffeFactory));
const GiraffeEcosystem = lazyPage(() => import('./pages/GiraffeEcosystem').then((m) => m.GiraffeEcosystem));
const UsydIndustrialisedConstruction = lazyPage(() => import('./pages/UsydIndustrialisedConstruction').then((m) => m.UsydIndustrialisedConstruction));

// Static route titles — dynamic pages (use cases, blog posts, etc.) set their own
const pageTitles: Record<string, string> = {
  '/': 'Giraffe Developer — the development management platform',
  '/giraffe-developer': 'Giraffe Developer — the development management platform',
  '/home': 'Giraffe — The Orchestration Layer for AI-Driven Real Estate',
  '/enterprise': 'Enterprise — Giraffe at Scale',
  '/plugins': 'Plugins and Data Packs — Giraffe',
  '/use-cases': 'Solutions — Giraffe',
  '/government': 'Government — Giraffe',
  '/demo': 'Live Demo — Giraffe',
  '/blog': 'Blog — Giraffe',
  '/contact': 'Contact — Giraffe',
  '/team': 'Team — Giraffe',
  '/case-studies': 'Case Studies — Giraffe',
  '/platform': 'AI for Real Estate Development | Giraffe',
  '/ai-orchestration': 'AI Orchestration — Giraffe',
  '/giraffe-app-platform': 'App Platform — Build Real Estate Apps | Giraffe',
  '/factory': 'Giraffe is a software factory',
  '/events/usyd-industrialised-construction':
    'Opportunities to Accelerate Industrialised Housing Construction Using Digital Technologies — Giraffe',
  '/usyd':
    'Opportunities to Accelerate Industrialised Housing Construction Using Digital Technologies — Giraffe',
  '/giraffe-design': 'Giraffe Design — CAD for strategic design',
  '/work-with-giraffe': 'Working with Giraffe',
  '/giraffe-city': 'Giraffe City — plan your city on Giraffe',
  '/giraffe-code': 'Giraffe Code — build and ship real estate and design apps',
  '/giraffe-ecosystem': 'The Giraffe ecosystem',
  '/legal/eula': 'EULA — Giraffe',
  '/legal/land-iq-eula': 'LandIQ EULA — Giraffe',
  '/legal/customer-app-policy': 'Customer App Acceptable Use Policy — Giraffe',
  '/legal/privacy': 'Privacy Policy — Giraffe',
  '/legal/security': 'Security — Giraffe',
};

// Page view tracker + scroll restoration
function PageTracker() {
  const [location] = useLocation();
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    // Set document title — static pages from map, dynamic pages override in their own useEffect
    document.title = pageTitles[location] || 'Giraffe';
    trackPageView(location);
    if (window.location.hash) {
      // A lazily loaded page may not have rendered yet: look for the hash target
      // for up to two seconds, then scroll to it
      const hash = window.location.hash;
      let tries = 0;
      const find = () => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
        else if (++tries < 40) setTimeout(find, 50);
      };
      requestAnimationFrame(find);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location, trackPageView]);

  return null;
}

type Ctx = { region: Region; setRegion: (r: Region) => void };
type RouteDef = {
  path: string;
  page?: LazyPage;
  render?: (params: Record<string, string>, ctx: Ctx) => JSX.Element;
};

const comingSoon = (title: string) => () => (
  <div class="container" style={{ padding: 'var(--space-16) 0' }}><h1>{title}</h1><p>Coming soon.</p></div>
);

// One table drives both the rendered routes and which page chunks to fetch
// before the first render. Like wouter's bare <Route>s, every match renders.
const routes: RouteDef[] = [
  // the home page is Giraffe Developer; the old home is kept at /home
  { path: '/', page: GiraffeDeveloper, render: (_, c) => <GiraffeDeveloper region={c.region} /> },
  { path: '/giraffe-developer', page: GiraffeDeveloper, render: (_, c) => <GiraffeDeveloper region={c.region} /> },
  { path: '/home', page: Home },
  { path: '/enterprise', page: Enterprise, render: (_, c) => <Enterprise region={c.region} onRegionChange={c.setRegion} /> },

  // Platform routes — /platform redirects to /ai-for-real-estate (core platform page)
  { path: '/platform', page: PlatformDetail, render: () => <PlatformDetail slug="ai-for-real-estate" /> },
  { path: '/platform/:section', page: PlatformDetail, render: () => <PlatformDetail slug="ai-for-real-estate" /> },
  { path: '/ai-orchestration', page: AiOrchestration },
  { path: '/giraffe-app-platform', page: GiraffeAppPlatform },
  // self-serve is retired: its old links land on Giraffe Design's pricing
  { path: '/self-serve', render: () => <Redirect to="/giraffe-design#pricing" replace /> },

  // Platform detail pages (from structure.md → platform-pages.json)
  ...platformPageSlugs.filter((s) => s !== 'giraffe-app-platform').map((slug) => (
    { path: `/${slug}`, page: PlatformDetail, render: () => <PlatformDetail slug={slug} /> }
  )),

  // Plugins and Data Packs
  { path: '/plugins', page: Apps },
  { path: '/plugins/:slug', page: Apps },

  // Use Cases
  { path: '/use-cases', page: UseCases, render: (_, c) => <UseCases region={c.region} /> },
  { path: '/use-cases/:slug', page: UseCaseDetail, render: (p) => <UseCaseDetail slug={p.slug} /> },

  // Government — bespoke sector landing page
  { path: '/government', page: Government },

  // Events
  { path: '/events/usyd-industrialised-construction', page: UsydIndustrialisedConstruction },
  { path: '/usyd', page: UsydIndustrialisedConstruction },

  // Sector / discipline pages (top-level)
  ...sectorSlugs.map((slug) => ({ path: `/${slug}`, page: UseCaseDetail, render: () => <UseCaseDetail slug={slug} /> })),

  // Pillar landing pages — only those NOT already handled by PlatformDetail
  ...pillarSlugs.filter((s) => !platformPageSlugs.includes(s)).map((slug) => (
    { path: `/${slug}`, page: UseCaseDetail, render: () => <UseCaseDetail slug={slug} /> }
  )),

  // the Academy is retired: its pages land on the home page
  { path: '/academy/:slug', render: () => <Redirect to="/" replace /> },

  // Case Studies
  { path: '/case-studies', page: CaseStudies },
  { path: '/case-studies/:slug', page: CaseStudyDetail, render: (p) => <CaseStudyDetail slug={p.slug} /> },

  // Resources - placeholder routes
  { path: '/resources/sdk', render: comingSoon('SDK') },
  { path: '/resources/knowledge-base', render: comingSoon('Knowledge Base') },
  { path: '/academy', render: () => <Redirect to="/" replace /> },
  { path: '/resources/academy', render: () => <Redirect to="/" replace /> },
  { path: '/resources/training', render: comingSoon('Training') },

  { path: '/demo', page: Demo },
  { path: '/blog', page: Blog },
  { path: '/blog/:slug', page: BlogPost },
  { path: '/contact', page: Contact },
  // pricing lives on each product page now: the old page's links land on Giraffe Design's
  { path: '/pricing', render: () => <Redirect to="/giraffe-design#pricing" replace /> },
  { path: '/pricing-draft', page: PricingDraft },
  { path: '/pricing/draft', page: PricingDraft },
  { path: '/appstore-draft', page: AppStoreDraft },
  { path: '/appstore/draft', page: AppStoreDraft },
  { path: '/flow-draft', page: FlowDraft },
  { path: '/flow/draft', page: FlowDraft },
  { path: '/homepage-draft', page: HomeDraft },
  { path: '/homepage/draft', page: HomeDraft },
  { path: '/homepage-draft-2', page: HomeDraft2 },
  { path: '/homepage/draft-2', page: HomeDraft2 },
  { path: '/homepage-draft-3', page: HomeDraft3 },
  { path: '/homepage/draft-3', page: HomeDraft3 },
  { path: '/careers', page: Careers },
  { path: '/careers/:slug', page: RoleDetail, render: (p) => <RoleDetail slug={p.slug} /> },
  { path: '/careers-draft', page: Careers },
  { path: '/legal/eula', page: Eula },
  { path: '/legal/land-iq-eula', page: LandIqEula },
  { path: '/legal/customer-app-policy', page: CustomerAppPolicy },
  { path: '/legal/privacy', page: PrivacyPolicy },
  { path: '/legal/security', page: Security },
  { path: '/team', page: Team },
  { path: '/siteguide', page: SiteGuide },
  { path: '/build', page: LandIQ },
  // The drafting-set pages bring their own nav and footer
  { path: '/giraffe-design', page: GiraffeDesign },
  { path: '/giraffe-city', page: GiraffeGovernment },
  { path: '/giraffe-code', page: GiraffeCode },
  { path: '/giraffe-ecosystem', page: GiraffeEcosystem },
  { path: '/factory', page: GiraffeFactory },
  { path: '/work-with-giraffe', page: WorkWithGiraffe },
];

// Fetch the chunks for every page matching a path.
function loadPagesFor(path: string): Promise<unknown> {
  return Promise.all(routes.filter((r) => r.page && matchesPath(r.path, path)).map((r) => r.page!.load()));
}

// The SSR build renders synchronously, so it loads every page first.
function loadAllPages(): Promise<unknown> {
  return Promise.all([...new Set(routes.map((r) => r.page).filter(Boolean))].map((p) => p!.load()));
}

function App() {
  const { region, setRegion } = useRegion();
  const [location] = useLocation();
  const path = location.replace(/\/$/, '') || '/';
  // the drafting pages are dark, so the nav over them is too
  const darkNav = ['/giraffe-design', '/giraffe-code', '/work-with-giraffe', '/factory'].includes(path);
  // ...and over City (light blue) and Code it takes the page's own colours
  // (and Developer's deeper sage)
  const isDeveloper = path === '/' || path === '/giraffe-developer';
  const navTone = path === '/giraffe-city' ? 'green' : path === '/giraffe-code' ? 'brown' : isDeveloper ? 'sage' : undefined;
  // the footer takes the page's own ground too
  const footerTone = path === '/giraffe-city' ? 'green' : path === '/giraffe-code' ? 'brown' : isDeveloper ? 'sage' : darkNav ? 'dark' : 'light';
  // the ecosystem is a full screen of its own, with its own nav, and no footer; the demo has no footer
  const isEcosystem = path === '/giraffe-ecosystem';
  const hideFooter = location === '/demo' || isEcosystem;
  const hideHeader = isEcosystem;
  const ctx: Ctx = { region, setRegion };

  return (
    <Layout region={region} onRegionChange={setRegion} hideFooter={hideFooter} hideHeader={hideHeader} darkNav={darkNav} navTone={navTone} footerTone={footerTone}>
      {routes.map((r, i) => (
        <Route key={i} path={r.path}>
          {(params: Record<string, string>) => {
            if (r.render) return r.render(params, ctx);
            const Page = r.page!;
            return <Page />;
          }}
        </Route>
      ))}
    </Layout>
  );
}

// Plain <a href="/…"> links (the nav, the home page's markup, the drafting and
// use case pages) would reload the whole page, and in dev the styles only come
// in after the scripts, so the bare page flashes between pages. Clicks on
// internal links to the app's own routes go through the router instead; links
// to the standalone static pages, to other sites, to new tabs and to anchors
// on the same page are left alone.
function LinkCatcher() {
  const [, navigate] = useLocation();
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a');
      if (!a || a.target || a.hasAttribute('download') || !a.getAttribute('href')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.hash) return;
      if (!routes.some((r) => matchesPath(r.path, url.pathname))) return;
      e.preventDefault();
      navigate(url.pathname + url.search + url.hash);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [navigate]);
  return null;
}

function AppRouter() {
  return (
    <Router>
      <PageTracker />
      <LinkCatcher />
      <App />
    </Router>
  );
}

export { App, loadAllPages };

if (typeof document !== 'undefined') {
  // Render only once this page's chunk is in, so the prerendered HTML stays on
  // screen until the same content replaces it. Render anyway if a chunk fails.
  loadPagesFor(window.location.pathname)
    .catch(() => {})
    .then(() => render(<AppRouter />, document.getElementById('app')!));
}
